import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyLocationsDetailOutDTO, LocationContactOutDTO, Pageable } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import COText from '@app/pages/COText/COText.tsx';
import SectionEmptyState from '@app/pages/customer/LocalityDetail/sections/SectionEmptyState.tsx';
import AddEditContactPersonSidebar from '@app/pages/customer/LocalityDetail/sidebars/AddEditContactPersonSidebar.tsx';
import AddEditLocationSidebar from '@app/pages/customer/LocalityList/sidebars/AddEditLocationSidebar.tsx';
import { isNotBlank } from '@utils/utils.tsx';
import SvgUser from '@icons/user-filled.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgTrashBin from '@icons/delete-trash.svg?react';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

interface ContactsFilter {
  q?: string;
  automaticPrefillToNotify?: string;
}

const ContactsSection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const { listLocationContacts, deleteLocationContacts } = useContext(ApiContext);
  const contactsTableRef = useRef<{ reloadData: () => void; selectedObjects: LocationContactOutDTO[]; clear: () => void }>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [editContact, setEditContact] = useState<LocationContactOutDTO>();
  const [deleteContact, setDeleteContact] = useState<LocationContactOutDTO>();
  // Editing the (pretend) main contact routes to the legacy whole-location sidebar — the only place the flat main is writable.
  const [legacyEditVisible, setLegacyEditVisible] = useState(false);

  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const readOnly = location.isDeleted;

  useEffect(() => {
    contactsTableRef.current?.reloadData();
  }, [location]);

  // The legacy whole-DTO location save recreates contacts and invalidates ids — any PUT/DELETE error means a stale list, so refetch.
  const refreshAfterStale = () => {
    contactsTableRef.current?.clear();
    contactsTableRef.current?.reloadData();
    onReload();
  };

  // The legacy flat main-contact is rendered as a *pretend* contact row (no contactId), injected client-side as the first
  // row of page 1. It carries no id so it's identifiable downstream (no checkbox / no delete / edit → legacy whole-DTO sidebar).
  // Real merge + set-as-main needs the BE migration (mainContactId); this is the interim until then.
  const mainContactRow: LocationContactOutDTO | undefined = isNotBlank(location.contactName)
    ? ({
        contactId: undefined,
        contactName: location.contactName,
        contactEmail: location.contactEmail,
        contactPhone: location.contactPhone,
        automaticPrefillToNotify: location.contactAutomaticPrefillToNotify,
      } as LocationContactOutDTO)
    : undefined;
  const isMainRow = (c: { contactName?: string; contactEmail?: string }) =>
    !!mainContactRow && c.contactName === mainContactRow.contactName && c.contactEmail === mainContactRow.contactEmail;

  // The main row is client-side, so the server filters never see it — mirror the active filters here so it
  // appears/disappears consistently with the other rows (approximation of the server's `q` = contains on name/email/phone).
  const mainMatchesFilter = (filter: ContactsFilter): boolean => {
    if (!mainContactRow) return false;
    const q = filter?.q?.trim().toLowerCase();
    if (q) {
      const haystack = `${mainContactRow.contactName ?? ''} ${mainContactRow.contactEmail ?? ''} ${mainContactRow.contactPhone ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (filter?.automaticPrefillToNotify === 'true' && mainContactRow.automaticPrefillToNotify !== true) return false;
    if (filter?.automaticPrefillToNotify === 'false' && mainContactRow.automaticPrefillToNotify !== false) return false;
    return true;
  };

  const loadContacts = (filter: ContactsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<LocationContactOutDTO>) => void) => {
    const injectMain = mainMatchesFilter(filter) && (pageable.pageNumber || 1) === 1;
    listLocationContacts(
      location.id,
      { pageNumber: pageable.pageNumber || 1, pageSize: pageable.pageSize || 10 },
      isNotBlank(filter?.q) ? filter.q : null,
      filter?.automaticPrefillToNotify === 'true' ? true : filter?.automaticPrefillToNotify === 'false' ? false : null,
      data => {
        const deduped = data.data.filter(c => !isMainRow(c));
        onSuccess({
          ...data,
          data: injectMain ? [mainContactRow, ...deduped] : deduped,
          total: injectMain ? data.total + 1 : data.total,
        });
      },
    );
  };

  const removeContacts = (contactIds: string[]) => {
    deleteLocationContacts(location.id, contactIds, () => refreshAfterStale(), null, {
      onError: (_e, showStandardError) => {
        showStandardError();
        refreshAfterStale();
      },
    });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('LocalityDetail.userFilter', 'User'),
      value: ['q'],
      dataTestId: 'location-contacts-filter-user',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('LocalityDetail.notificationFilter', 'Notification'),
      value: ['automaticPrefillToNotify'],
      options: [
        { label: tr('generic.yes', 'Yes'), value: 'true', active: true },
        { label: tr('generic.no', 'No'), value: 'false', active: true },
      ],
      dataTestId: 'location-contacts-filter-notification',
    },
  ];

  const contactColumnsConfig: ColumnConfig<LocationContactOutDTO>[] = [
    {
      id: 'contactName',
      header: 'LocalityDetail.contactName',
      headerDefault: 'Name',
      width: '22%',
      order: 1,
      render: row => <span>{row.contactName}</span>,
    },
    {
      id: 'contactEmail',
      header: 'LocalityDetail.contactEmail',
      headerDefault: 'E-mail',
      width: '28%',
      order: 2,
      render: row => <span>{row.contactEmail}</span>,
    },
    {
      id: 'contactPhone',
      header: 'LocalityDetail.contactPhone',
      headerDefault: 'Phone',
      width: '18%',
      order: 3,
      render: row => <span>{row.contactPhone}</span>,
    },
    {
      id: 'automaticPrefillToNotify',
      header: 'LocalityDetail.automaticPrefillToNotify',
      headerDefault: 'Automatically select for notification',
      width: '22%',
      order: 4,
      render: row => (
        <COBadge variant={'filled'} type={row.automaticPrefillToNotify ? 'positive' : 'warning'} dataTestId={'location-contact-autoNotify'}>
          <COText label={row.automaticPrefillToNotify ? tr('generic.yes', 'Yes') : tr('generic.no', 'No')} />
        </COBadge>
      ),
    },
    {
      id: 'actions',
      width: '10%',
      order: 5,
      render: row => {
        // No contactId = the pretend main contact: edit routes to the legacy sidebar, and it cannot be deleted.
        const isMain = !row.contactId;
        if (readOnly) return null;
        return (
          <div className={'flex flex-row justify-end gap-2'}>
            <RingilButton
              mode={'table'}
              icon={<SvgPencil />}
              onClick={() => {
                if (isMain) {
                  setLegacyEditVisible(true);
                } else {
                  setEditContact(row);
                  setSidebarVisible(true);
                }
              }}
              dataTestId={isMain ? 'location-contact-edit-main' : `location-contact-edit-${row.contactId}`}
            />
            {/* Main contact can't be deleted: hide the icon (visibility:hidden) but keep its box so the pencil stays column-aligned with other rows. */}
            <span className={isMain ? 'invisible' : undefined}>
              <RingilButton
                mode={'table'}
                icon={<SvgTrashBin />}
                onClick={() => setDeleteContact(row)}
                dataTestId={`location-contact-delete-${row.contactId}`}
              />
            </span>
          </div>
        );
      },
    },
  ];

  const massActionConfig: MassActionConfig<LocationContactOutDTO>[] = [
    {
      label: tr('LocalityDetail.remove', 'Remove'),
      icon: <SvgTrashBin />,
      onClick: () => {
        // filter(Boolean) drops the id-less main row as a safety net (it has no checkbox, so it shouldn't be selectable anyway).
        const ids = (contactsTableRef.current?.selectedObjects ?? []).map(c => c.contactId).filter(Boolean);
        if (ids.length > 0) removeContacts(ids);
      },
    },
  ];

  const contactsHeading = (
    <span className="flex flex-row items-center gap-2">
      {tr('LocalityDetail.contactPersons', 'Contact persons')}
      {!readOnly && (
      <RingilButton
        mode={'link'}
        icon={<SvgPlus />}
        label={tr('LocalityDetail.addPerson', 'Add person')}
        onClick={() => {
          setEditContact(undefined);
          setSidebarVisible(true);
        }}
        dataTestId={'location-contacts-add'}
      />
      )}
    </span>
  );

  // Table shows whenever there's a (pretend) main contact or any sub-resource contact; empty state only when neither exists.
  const hasContacts = !!mainContactRow || (location.contacts ?? []).filter(c => !isMainRow(c)).length > 0;

  return (
    <div className={'flex flex-col gap-4'}>
      <SectionPanel icon={<SvgUser className={'w-6 h-6'} />} iconColor={'var(--content-tertiary)'} heading={contactsHeading}>
        {hasContacts ? (
          <RingilDataTable
            ref={contactsTableRef}
            columnConfig={contactColumnsConfig as ColumnConfig<unknown>[]}
            filtersConf={filtersConf}
            loadDataFromApi={loadContacts}
            filterName={'location_detail_contacts'}
            massActionConfig={readOnly ? undefined : (massActionConfig as MassActionConfig<unknown>[])}
            isRowSelectable={(row: unknown) => !!(row as LocationContactOutDTO).contactId}
          />
        ) : (
          <SectionEmptyState
            message={tr('LocalityDetail.noContactPerson', 'No contact person added.')}
            buttonLabel={readOnly ? undefined : tr('LocalityDetail.addPerson', 'Add person')}
            onButtonClick={
              readOnly
                ? undefined
                : () => {
                    setEditContact(undefined);
                    setSidebarVisible(true);
                  }
            }
            dataTestId={'location-contacts-empty'}
          />
        )}
      </SectionPanel>

      <AddEditContactPersonSidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        onCompleted={() => {
          setSidebarVisible(false);
          refreshAfterStale();
        }}
        locationId={location.id}
        editContact={editContact}
      />

      <AddEditLocationSidebar
        visible={legacyEditVisible}
        onHide={() => setLegacyEditVisible(false)}
        onCompleted={() => {
          setLegacyEditVisible(false);
          refreshAfterStale();
        }}
        locationId={location.id}
      />

      <ConfirmDialog
        visible={!!deleteContact}
        dialogTitle={tr('LocalityDetail.deleteContact', 'Delete contact person')}
        dialogMessage={tr('LocalityDetail.deleteContactMessage', 'Do you want to delete this contact person?')}
        onConfirm={() => {
          removeContacts([deleteContact.contactId]);
          setDeleteContact(undefined);
        }}
        onCancel={() => setDeleteContact(undefined)}
        onAcceptButtonText={tr('generic.delete', 'Delete')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
      />
    </div>
  );
};

export default ContactsSection;
